import { normalizeProductionUnit, quantityTotals, sumKnownQuantities } from "../../../shared/production/quantity";
import { downtimeReportSummary } from "../../../shared/production/downtime";
import type { ProductionReferenceData, ProductionRun } from "./models";

const EPSILON = 0.000_001;

export type ProductionProjection = {
  plannedQuantity: number | null;
  producedQuantity: number | null;
  projectedQuantity: number | null;
  attainment: number | null;
  unit: string | null;
  comparable: boolean;
};

const normalizedUnit = normalizeProductionUnit;

export const productionQuantity = (run: ProductionRun) => quantityTotals(run.entries, run.unit).production;

export const netProductionMinutes = (run: ProductionRun, now = Date.now()) => {
  if (run.planItemKind === "ACTIVITY") return 0;
  if (!run.startedAt) return 0;
  const end = run.closedAt ? new Date(run.closedAt).getTime() : now;
  const downtime = downtimeReportSummary(run.downtimes, end, run.reportWindow).totalMilliseconds;
  return Math.max(0, end - new Date(run.startedAt).getTime() - downtime) / 60_000;
};

// Backward-compatible export: an ADET stock unit is one ADET, not a guessed chocolate/piece.
export const piecesPerQuantityUnit = (run: ProductionRun) => normalizedUnit(run.unit) === "ADET" ? 1 : null;

const plannedMinutesForRun = (run: ProductionRun, reference: ProductionReferenceData | null) => {
  const schedule = reference?.scheduleItems.find(
    (item) => item.type === "WORK_ORDER" && item.date === run.shiftDate &&
      item.shiftCode === run.shiftCode && item.workOrderNo === run.workOrderNo &&
      (!item.productCode || item.productCode === run.productCode),
  );
  if (schedule?.plannedHours && schedule.plannedHours > 0) return schedule.plannedHours * 60;
  if (!run.plannedStartAt || !run.plannedEndAt) return null;
  const duration = (new Date(run.plannedEndAt).getTime() - new Date(run.plannedStartAt).getTime()) / 60_000;
  return duration > 0 ? duration : null;
};

export const rateMetrics = (
  runs: ProductionRun[],
  reference: ProductionReferenceData | null,
  now = Date.now(),
) => {
  const included = runs.filter((run) => run.disposition !== "CANCELLED" && run.planItemKind !== "ACTIVITY");
  const units = new Set(included.map((run) => normalizedUnit(run.unit)));
  const unit = units.size === 1 ? included[0]?.unit ?? null : null;
  if (!unit || unit === "plan birimi") return { plannedRate: null, actualRate: null, performance: null, unit };
  let plannedQuantity = 0, plannedMinutes = 0, actualQuantity = 0, netMinutes = 0;
  let plannedUnknown = false, actualUnknown = false;
  for (const run of included) {
    const minutes = plannedMinutesForRun(run, reference), actual = productionQuantity(run);
    if (!minutes || run.plannedQuantity == null) plannedUnknown = true;
    else { plannedQuantity += run.plannedQuantity; plannedMinutes += minutes; }
    if (actual == null) actualUnknown = true;
    else { actualQuantity += actual; netMinutes += netProductionMinutes(run, now); }
  }
  const plannedRate = !plannedUnknown && plannedMinutes > 0 ? plannedQuantity / plannedMinutes : null;
  const actualRate = !actualUnknown && netMinutes > 0 ? actualQuantity / netMinutes : null;
  return { plannedRate, actualRate, performance: plannedRate && actualRate != null ? actualRate / plannedRate * 100 : null, unit };
};

export const productionProjection = (
  runs: ProductionRun[],
  reference: ProductionReferenceData | null,
  horizonMinutes: number,
  now = Date.now(),
): ProductionProjection => {
  const included = runs.filter((run) => run.disposition !== "CANCELLED" && run.plannedQuantity != null);
  const units = new Set(included.map((run) => normalizedUnit(run.unit)));
  const plannedQuantity = included.reduce((sum, run) => sum + (run.plannedQuantity ?? 0), 0);
  const producedQuantity = sumKnownQuantities(included.map(productionQuantity));
  if (!included.length || units.size !== 1 || plannedQuantity <= EPSILON || producedQuantity === null) {
    return { plannedQuantity: units.size === 1 ? plannedQuantity : null, producedQuantity: units.size === 1 ? producedQuantity : null, projectedQuantity: null, attainment: null, unit: units.size === 1 ? included[0]?.unit ?? null : null, comparable: false };
  }

  let projectedQuantity = 0;
  const byLine = new Map<string, ProductionRun[]>();
  for (const run of included) byLine.set(run.lineId, [...(byLine.get(run.lineId) ?? []), run]);
  for (const lineRuns of byLine.values()) {
    let availableMinutes = Math.max(0, horizonMinutes);
    const ordered = [...lineRuns].sort((left, right) => {
      const state = (run: ProductionRun) => run.status === "ACTIVE" ? 0 : run.status === "READY" ? 1 : 2;
      return state(left) - state(right) || left.sequenceNo - right.sequenceNo;
    });
    for (const run of ordered) {
      const produced = productionQuantity(run);
      if (produced === null) return { plannedQuantity, producedQuantity: null, projectedQuantity: null, attainment: null, unit: null, comparable: false };
      const plan = run.plannedQuantity ?? 0;
      projectedQuantity += produced;
      if (run.status === "COMPLETED" || produced >= plan - EPSILON || availableMinutes <= 0) continue;
      const actualMinutes = netProductionMinutes(run, now);
      const plannedMinutes = plannedMinutesForRun(run, reference);
      const quantityPerMinute = run.status === "ACTIVE" && produced > EPSILON && actualMinutes > EPSILON
        ? produced / actualMinutes
        : plannedMinutes && plannedMinutes > 0 ? plan / plannedMinutes : 0;
      if (quantityPerMinute <= 0) continue;
      const remaining = Math.max(0, plan - produced);
      const producible = Math.min(remaining, quantityPerMinute * availableMinutes);
      projectedQuantity += producible;
      availableMinutes -= producible / quantityPerMinute;
    }
  }
  return {
    plannedQuantity,
    producedQuantity,
    projectedQuantity,
    attainment: (projectedQuantity / plannedQuantity) * 100,
    unit: included[0].unit,
    comparable: true,
  };
};
